import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { computeBattleTooltipPlacement } from '../utils/battleTooltipViewportPlacement';
import '../components/mobile/MobileTooltipLayer.css';

/**
 * Viewport-clamped portaled tooltip for PvP rank badges in BattleUI.
 * Escapes HUD scale transforms and keeps the label fully on-screen.
 */
export function BattleRankPortaledTooltip({ anchorRef, text, preferBottom, open }) {
  const tooltipRef = useRef(null);
  const [placement, setPlacement] = useState({ left: 0, top: 0, ready: false });

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    const tip = tooltipRef.current;
    if (!anchor || !tip) return;

    const rect = anchor.getBoundingClientRect();
    const tipWidth = tip.offsetWidth;
    const tipHeight = tip.offsetHeight;
    if (!tipWidth || !tipHeight) return;

    const next = computeBattleTooltipPlacement(rect, tipWidth, tipHeight, { preferBottom });
    setPlacement({ left: next.left, top: next.top, ready: true });
  }, [anchorRef, preferBottom]);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement({ left: 0, top: 0, ready: false });
      return undefined;
    }

    const raf = requestAnimationFrame(reposition);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition, text]);

  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(() => requestAnimationFrame(reposition));
    return () => cancelAnimationFrame(raf);
  }, [open, reposition, text]);

  if (!open || !text || typeof document === 'undefined' || !document.body) {
    return null;
  }

  return createPortal(
    <div
      ref={tooltipRef}
      className="mobile-tooltip-layer battle-rank-portaled-tooltip"
      role="tooltip"
      style={{
        left: `${placement.left}px`,
        top: `${placement.top}px`,
        opacity: placement.ready ? 1 : 0,
      }}
    >
      {text}
    </div>,
    document.body
  );
}
